import type {
  AutoBidNotOrderedMarket,
  AutoBidNotOrderedNotice,
} from '@ebalance/proto/bffuser/v1/auto_bid_notice_model_pb'

/**
 * 警告バナーの 1 行 = 1 札 (受渡日 × エリア × 発注主体 × コマ × 売買)。
 * `notices` は同じ札の記録 (理由ごとに 1 件) を応答の順に持つ。 1 件以上。
 */
export type AutoBidNoticeBannerRow = {
  /** 札ごとに一意な値 (一覧の React key 用)。 */
  key: string
  notices: AutoBidNotOrderedNotice[]
}

const bidKey = (n: AutoBidNotOrderedNotice): string =>
  [
    n.targetDate === undefined
      ? '?'
      : `${n.targetDate.year}-${n.targetDate.month}-${n.targetDate.day}`,
    n.areaCode,
    n.issuerType,
    n.timeCode,
    n.side,
  ].join(':')

/**
 * 警告バナーの一覧の行を組み立てる。 見出しの {N} は戻り値の行の数 (AC-BIN-117 / AC-BSP-120、
 * `docs/specs/conventions.md` §警告・案内で対象を列挙する時の件数と上限)。
 *
 * - 記録は `market` の分だけを対象にする (他の市場は数えず出さない)。 応答は表示期間内で
 *   未解消の記録だけなので、市場以外 (受渡日・エリア・発注主体の選択) では絞らない
 * - 記録は理由ごとに 1 件なので、同じ札の記録は 1 行にまとめる (理由が違っても 1 件と数える)。
 * - 並べ替えはしない。 行の順序は、その札の最初の記録が応答に現れた順 (応答は backend が
 *   締切前の札を先、締切を過ぎた札を後に、それぞれ受渡日 → コマ → エリアの順で返す)
 */
export const buildAutoBidNoticeBannerRows = (
  notices: readonly AutoBidNotOrderedNotice[],
  market: AutoBidNotOrderedMarket,
): AutoBidNoticeBannerRow[] => {
  const rows = new Map<string, AutoBidNoticeBannerRow>()
  for (const n of notices) {
    if (n.market !== market) continue
    const key = bidKey(n)
    const row = rows.get(key)
    if (row === undefined) rows.set(key, { key, notices: [n] })
    else row.notices.push(n)
  }
  return [...rows.values()]
}
